'use client';

/**
 * Passformen ur TecDoc på ett produktkort som ritas på servern (katalogens märkes- och
 * modellsidor). Korten på samma sida samlas till ETT anrop: varje etikett anmäler sitt
 * produkt-id, och efter 60 ms går frågan för alla på en gång.
 */
import { useEffect, useState } from 'react';
import { useCartStore } from '@/stores/cart';
import { api } from '@/lib/api';
import { useLocale } from '@/hooks/useLocale';
import { t } from '@/i18n/t';
import type { KortPassform } from '@/types';

const svar = new Map<string, Promise<KortPassform | undefined>>();
let ko: { ktype: number; plate: string | null; ids: Map<number, (v: KortPassform | undefined) => void> } | null = null;

function fraga(id: number, ktype: number, plate: string | null): Promise<KortPassform | undefined> {
  const nyckel = `${id}:${ktype}:${plate ?? ''}`;
  const finns = svar.get(nyckel);
  if (finns) return finns;
  const p = new Promise<KortPassform | undefined>((losa) => {
    if (!ko || ko.ktype !== ktype || ko.plate !== plate) {
      const denna = { ktype, plate, ids: new Map<number, (v: KortPassform | undefined) => void>() };
      ko = denna;
      setTimeout(() => {
        if (ko === denna) ko = null;
        const ids = [...denna.ids.keys()];
        api.passformLista(ids, ktype, plate)
          .then((r) => denna.ids.forEach((f, i) => f(r.passform?.[i])))
          .catch(() => denna.ids.forEach((f) => f(undefined)));
      }, 60);
    }
    ko.ids.set(id, losa);
  });
  svar.set(nyckel, p);
  return p;
}

export function PassformEtikett({ productId }: { productId: number }) {
  const ktype = useCartStore((s) => s.selectedVehicle?.ktype);
  const plate = useCartStore((s) => s.selectedPlate);
  const { locale } = useLocale();
  const [pf, setPf] = useState<KortPassform | undefined>();
  useEffect(() => {
    setPf(undefined);
    if (!ktype) return;
    let aktuell = true;
    fraga(productId, ktype, plate ?? null).then((v) => { if (aktuell) setPf(v); });
    return () => { aktuell = false; };
  }, [productId, ktype, plate]);
  if (!pf) return null;
  return (
    <p title={locale === 'sv' ? pf.text : undefined}
      className={`mt-1 inline-flex max-w-full items-center rounded px-1.5 py-0.5 text-[10px] sm:text-xs font-semibold ${
        pf.status === 'passar' ? 'bg-green-50 text-green-800 border border-green-200'
        : pf.status === 'villkor' ? 'bg-amber-50 text-amber-900 border border-amber-200'
        : 'bg-red-50 text-red-800 border border-red-200'}`}>
      <span className="truncate">
        {pf.status === 'passar' ? t(locale, 'product.fitExact')
          : locale === 'sv' ? pf.text
          : t(locale, pf.status === 'villkor' ? 'product.fitCondition' : 'product.fitNo')}
      </span>
    </p>
  );
}
